import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from './App';
import { initAnalytics } from './lib/analytics';
import './styles/global.css';

initAnalytics();

// iOS Safari ignores `user-scalable=no` since iOS 10. Without these
// listeners, double-tap and pinch on a game canvas zooms the page and
// the user gets stuck zoomed in (no UI to zoom back out). Block at the
// document level so it covers every route, every game.
(function installNoZoom() {
  const block = (e: Event) => { e.preventDefault(); };
  // iOS pinch — Safari emits non-standard 'gesture*' events
  document.addEventListener('gesturestart', block, { passive: false });
  document.addEventListener('gesturechange', block, { passive: false });
  document.addEventListener('gestureend', block, { passive: false });

  // NOTE: we deliberately do NOT install a global `touchend` double-tap
  // preventDefault handler. On iOS, calling preventDefault on touchend
  // also suppresses the *synthetic click* the browser would fire — so
  // any two-button workflow done in quick succession (e.g. trapline:
  // tap SLOW, tap grid to plant) loses the second click. iOS Safari's
  // double-tap-zoom is already disabled by viewport
  // `maximum-scale=1.0, user-scalable=no`; relying on that is safer
  // than a JS handler that breaks rapid taps.

  // Defensive: if visualViewport ever reports a zoom (e.g. from a stray
  // gesture before we attached), force-scroll back to scale 1 by
  // dispatching a resize. Modern iOS exposes scale on visualViewport.
  const vv = (window as Window & { visualViewport?: VisualViewport }).visualViewport;
  if (vv) {
    vv.addEventListener('resize', () => {
      if (vv.scale > 1.01) {
        // Best-effort: blur active element (collapses iOS keyboard zoom)
        // and reset scroll. Apple does not expose a programmatic
        // un-zoom, but blurring + scrolling to 0 typically resets it.
        (document.activeElement as HTMLElement | null)?.blur?.();
        window.scrollTo(0, 0);
      }
    });
  }
})();

const root = document.getElementById('root');
if (!root) throw new Error('#root not found');
ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
